import { useCallback, useEffect, useState } from 'react';
import type { ListaRecordatorios, Recordatorio } from '../../api/ops/entregas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoNoContratado, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { rolesDe } from '../../logica/acceso';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { rutaDeApartado } from '../apartados';
import type { PropsPantalla } from '../tipos';
import { MAXIMO_MOMENTOS, PERMISOS_MENSAJES, errorDeAntelaciones, minutosDe, textoAntelaciones, textoMomento, type Unidad } from './reglas';
import { AYUDA_RECORDATORIOS } from './textos';

/**
 * «Recordatorios» (5.4): una fila por tipo que declaran los módulos activos (`listReminderSettings`), con cuándo se envía
 * («1 día y 2 h antes»), por dónde (el canal preferido del cliente y, si no puede, email; R-04, solo lectura; «Por email»
 * en los que salen solo por email, G331) y su RV Interruptor. La ficha deja encender o apagar, quitar y añadir momentos
 * (de 1 a 3, de 15 minutos a 14 días, G33, G44): cada cambio sale a los 5 s con «Deshacer» (G159) con `If-Match` de su
 * versión (`0` la primera vez). Sin el bloque (`entitlement_active`), «no contratado». Valen para lo que se confirme
 * desde ahora.
 */
export function Recordatorios(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const puede = permisos.puede(PERMISOS_MENSAJES.recordatorios);
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [datos, setDatos] = useState<ListaRecordatorios | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [abierto, setAbierto] = useState<string | null>(null);
    const [anadir, setAnadir] = useState(false);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        ops.listReminderSettings(negocio)
            .then((l) => {
                if (vivo) {
                    setDatos(l);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede, intento]);

    useCabecera({ ayuda: puede ? AYUDA_RECORDATORIOS : [], acciones: [] });

    if (!puede) {
        return <EstadoSinPermiso seccion="Recordatorios" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && datos === null) {
        return <EstadoError seccion="los recordatorios" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="los recordatorios" />;
    }
    if (!datos.entitlement_active) {
        return (
            <EstadoNoContratado
                bloque="Recordatorios automáticos"
                incluye={['Recordatorio antes de cada reserva, cita o evento', 'Hasta 3 momentos, de 15 minutos a 14 días antes', 'Por el canal que prefiere cada cliente, sin gastar envíos']}
                propietario={membresia?.is_owner === true}
                onAnadir={null}
                onInicio={() => { navegar('/'); }}
                movil={movil}
            />
        );
    }

    /** Aplica un cambio al momento y lo guarda a los 5 s salvo «Deshacer». */
    const cambiar = (r: Recordatorio, cambio: { enabled: boolean; offsets_minutes: number[] }, texto: string) => {
        const fallo = errorDeAntelaciones(cambio.offsets_minutes);
        if (fallo !== null) {
            avisar({ tipo: 'error', texto: fallo });
            return;
        }
        const antes = datos;
        setDatos({ ...datos, data: datos.data.map((x) => (x.reminder_type === r.reminder_type ? { ...x, ...cambio } : x)) });
        envio.programar({
            texto,
            donde: 'ajuste',
            enviar: () => {
                ops.updateReminderSetting(negocio, r.reminder_type, cambio, r.version)
                    .then((nuevo) => {
                        setDatos((d) => (d === null ? d : { ...d, data: d.data.map((x) => (x.reminder_type === nuevo.reminder_type ? nuevo : x)) }));
                    })
                    .catch((e: unknown) => {
                        const breve = avisoBreveDeError(e);
                        avisar({ tipo: 'error', texto: breve ?? (e instanceof ErrorApi && e.estado === 412 ? 'Otra persona lo ha cambiado. Te enseñamos lo último.' : e instanceof ErrorApi && e.estado === 422 ? (e.problema.detail ?? 'Revisa los momentos.') : 'No he podido guardar el recordatorio. Prueba de nuevo.') });
                        recargar();
                    });
            },
            alDeshacer: () => {
                setDatos(antes);
            },
        });
    };

    const elegido = datos.data.find((r) => r.reminder_type === abierto) ?? null;
    const rutaPlantillas = rutaDeApartado(props, 'mensajes_plantillas', 'mensajes_');

    return (
        <div className="rv-ca-apartado">
            {envio.pendiente !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
                    <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                    <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                </div>
            )}
            <Tabla
                etiqueta="Recordatorios"
                movil={movil}
                columnas={[
                    { clave: 'recordatorio', titulo: 'Recordatorio' },
                    { clave: 'cuando', titulo: 'Cuándo se envía' },
                    { clave: 'donde', titulo: 'Por dónde' },
                    { clave: 'activo', titulo: 'Activo', alinear: 'derecha' },
                ]}
                filas={datos.data.map((r) => ({
                    clave: r.reminder_type,
                    etiqueta: r.title ?? r.reminder_type,
                    onAbrir: () => { setAbierto(r.reminder_type); },
                    celdas: {
                        recordatorio: (
                            <span className="rv-en-celda">
                                <strong>{r.title ?? r.reminder_type}</strong>
                                {r.is_default && <small>De Reservaria</small>}
                            </span>
                        ),
                        cuando: r.enabled ? textoAntelaciones(r.offsets_minutes) : 'Apagado',
                        donde: r.email_only === true ? 'Por email' : 'El que prefiere el cliente',
                        activo: (
                            <span onClick={(e) => { e.stopPropagation(); }}>
                                <Interruptor ariaLabel={`Enviar ${r.title ?? r.reminder_type}`} valor={r.enabled} desactivado={soloLectura} compacto linea="ninguna" onChange={(v) => { cambiar(r, { enabled: v, offsets_minutes: r.offsets_minutes }, v ? 'Recordatorio encendido.' : 'Recordatorio apagado.'); }} />
                            </span>
                        ),
                    },
                }))}
                vacia={<p className="rv-ca-nota rv-ca-nota--gris">Ningún módulo activo tiene recordatorios.</p>}
            />
            <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="info" /><span>Los cambios valen para lo que se confirme desde ahora. No gastan envíos de tu plan.</span></p>
            <PanelLateral abierto={elegido !== null} onCerrar={() => { setAbierto(null); }} movil={movil} titulo={elegido?.title ?? ''} eyebrow="Recordatorio" icono="calendar-check">
                {elegido !== null && (
                    <div className="rv-ca-cuerpo">
                        <Interruptor
                            etiqueta="Enviar este recordatorio"
                            ayuda={elegido.enabled ? `Sale ${textoAntelaciones(elegido.offsets_minutes)} del inicio.` : 'Apagado: no sale.'}
                            valor={elegido.enabled}
                            desactivado={soloLectura}
                            onChange={(v) => { cambiar(elegido, { enabled: v, offsets_minutes: elegido.offsets_minutes }, v ? 'Recordatorio encendido.' : 'Recordatorio apagado.'); }}
                        />
                        <div className="rv-ca-bloque">
                            <div className="rv-ca-bloque__cab"><strong>Cuándo se envía</strong></div>
                            {[...elegido.offsets_minutes].sort((a, b) => b - a).map((m) => (
                                <div key={m} className="rv-en-momento">
                                    <Icono nombre="clock" />
                                    <span>{`${textoMomento(m)} antes`}</span>
                                    {!soloLectura && elegido.offsets_minutes.length > 1 && (
                                        <button type="button" className="rv-bi rv-en-tipo__mas" aria-label={`Quitar ${textoMomento(m)} antes`} onClick={() => { cambiar(elegido, { enabled: elegido.enabled, offsets_minutes: elegido.offsets_minutes.filter((x) => x !== m) }, `Quitado: ${textoMomento(m)} antes.`); }}>
                                            <Icono nombre="x" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {!soloLectura && (
                                <div className="rv-en-botones" style={{ alignItems: 'center' }}>
                                    <Boton tipo="secundario" icono="plus" movil={movil} disabled={elegido.offsets_minutes.length >= MAXIMO_MOMENTOS} onClick={() => { setAnadir(true); }}>
                                        Añadir otro momento
                                    </Boton>
                                    <span className="rv-ca-nota rv-ca-nota--gris">Hasta 3, de 15 minutos a 14 días antes.</span>
                                </div>
                            )}
                        </div>
                        <div className="rv-ca-bloque">
                            <div className="rv-ca-bloque__cab"><strong>Por dónde sale</strong></div>
                            {elegido.email_only === true ? (
                                <div className="rv-ca-dato"><span>Siempre</span><strong>Email</strong></div>
                            ) : (
                                <>
                                    <div className="rv-ca-dato"><span>Primero</span><strong>El canal que prefiere el cliente</strong></div>
                                    <div className="rv-ca-dato"><span>Si no puede</span><strong>Email</strong></div>
                                </>
                            )}
                            <p className="rv-ca-nota rv-ca-nota--gris">{elegido.email_only === true ? 'Este recordatorio sale solo por email. Sin email del cliente, no sale.' : 'Sale por el canal que prefiere cada cliente (se elige en su ficha). No gasta envíos de tu plan.'}</p>
                        </div>
                        <div className="rv-ca-bloque">
                            <div className="rv-ca-bloque__cab"><strong>Texto</strong></div>
                            <div className="rv-ca-dato">
                                <span className="rv-en-chip-fila">{elegido.title}<Chip estado="neutral">{elegido.is_default ? 'De Reservaria' : 'Ajustado'}</Chip></span>
                                {rutaPlantillas !== null && (
                                    <Boton tipo="secundario" icono="pencil" movil={movil} onClick={() => { navegar(`${rutaPlantillas}&mensaje=${encodeURIComponent(elegido.message_type)}`); }}>
                                        Cambiar el texto
                                    </Boton>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </PanelLateral>
            {anadir && elegido !== null && (
                <VentanaMomento
                    actuales={elegido.offsets_minutes}
                    movil={movil}
                    onCerrar={() => { setAnadir(false); }}
                    onAnadir={(m) => {
                        setAnadir(false);
                        cambiar(elegido, { enabled: elegido.enabled, offsets_minutes: [...elegido.offsets_minutes, m] }, `Añadido: ${textoMomento(m)} antes.`);
                    }}
                />
            )}
        </div>
    );
}

const UNIDADES: readonly { valor: Unidad; texto: string }[] = [
    { valor: 'minutos', texto: 'Minutos' },
    { valor: 'horas', texto: 'Horas' },
    { valor: 'dias', texto: 'Días' },
];

function VentanaMomento({ actuales, movil, onCerrar, onAnadir }: { actuales: readonly number[]; movil: boolean; onCerrar: () => void; onAnadir: (minutos: number) => void }) {
    const [cantidad, setCantidad] = useState('2');
    const [unidad, setUnidad] = useState<Unidad>('horas');
    const numero = Number(cantidad.replace(',', '.'));
    const minutos = Number.isFinite(numero) && numero > 0 ? minutosDe(numero, unidad) : 0;
    const error = cantidad.trim() === '' ? null : errorDeAntelaciones([...actuales, minutos]);
    return (
        <Ventana
            abierta
            titulo="Añadir otro momento"
            texto="Cuánto antes del inicio sale el recordatorio."
            accion="Añadir"
            accionIcono="plus"
            inactiva={minutos === 0 || error !== null}
            movil={movil}
            onAccion={() => { onAnadir(minutos); }}
            onCerrar={onCerrar}
        >
            <div className="rv-en-cantidad">
                <Campo etiqueta="Cantidad" valor={cantidad} onChange={setCantidad} tipo="number" modo="numeric" error={error} />
                <Desplegable etiqueta="Unidad" valor={unidad} opciones={UNIDADES} onChange={(v) => { setUnidad(v as Unidad); }} />
            </div>
        </Ventana>
    );
}
